/* Copyright (c) 2015, FIRST.ORG, INC.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification, are permitted provided that the
 * following conditions are met:
 * 1. Redistributions of source code must retain the above copyright notice, this list of conditions and the following
 *    disclaimer.
 * 2. Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the
 *    following disclaimer in the documentation and/or other materials provided with the distribution.
 * 3. Neither the name of the copyright holder nor the names of its contributors may be used to endorse or promote
 *    products derived from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL,
 * SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY,
 * WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */

/* This JavaScript contains two main functions. Both take CVSSv2 metric values and calculate CVSSv2 scores for Base,
 * Temporal and Environmental metric groups, their associated severity ratings, and an overall Vector String.
 *
 * Use CVSSv2.calculateCVSSFromMetrics if you wish to pass metric values as individual parameters.
 * Use CVSSv2.calculateCVSSFromVector if you wish to pass metric values as a single Vector String.
 *
 * Changelog
 *
 * 2017-02-04  Masato Terada  Initial release for CVSSv2 2.0.
 *
 * 2015-08-04  Darius Wiles   Added CVSSv2.generateXMLFromMetrics and CVSSv2.generateXMLFromVector functions to return
 *                            XML string representations of: a set of metric values; or a Vector String respectively.
 *                            Moved all constants and functions to an object named "CVSSv2" to
 *                            reduce the chance of conflicts in global variables when this file is combined with
 *                            other JavaScript code. This will break all existing code that uses this file until
 *                            the string "CVSSv2." is prepended to all references. The "Exploitability" metric has been
 *                            renamed "Exploit Code Maturity" in the specification, so the same change has been made
 *                            in the code in this file.
 *
 * 2015-04-24  Darius Wiles   Environmental formula modified to eliminate undesirable behavior caused by subtle
 *                            differences in rounding between Temporal and Environmental formulas that often
 *                            caused the latter to be 0.1 lower than than the former when all Environmental
 *                            metrics are "Not defined". Also added a RoundUp1 function to simplify formulas.
 *
 * 2015-04-09  Darius Wiles   Added calculateCVSSFromVector function, license information, cleaned up code and improved
 *                            comments.
 *
 * 2014-12-12  Darius Wiles   Initial release for CVSSv2 3.0 Preview 2.
 */

// Constants used in the formula. They are not declared as "const" to avoid problems in older browsers.

var CVSSv2 = {};

CVSSv2.CVSSVersionIdentifier = "";
CVSSv2.exploitabilityCoefficient = 20;
CVSSv2.impactCoefficient = 10.41;

// A regular expression to validate that a CVSSv2 2.0 vector string is well formed. It checks metrics and metric
// values. It does not check that a metric is specified more than once and it does not check that all base
// metrics are present. These checks need to be performed separately.
CVSSv2.vectorStringRegex_20 = /^((AV:[NAL]|AC:[LMH]|Au:[NSM]|[CIA]:[NPC]|E:[NUPFH][DO]?[C]?|RL:[NOTWU][FD]?|RC:[NUC][DCR]?|CDP:[NLMH][MHD]?|TD:[NLMH]D?|[CIA]R:[NLMH]D?)\/)*(AV:[NAL]|AC:[LMH]|Au:[NSM]|[CIA]:[NPC]|E:[NUPFH][DO]?[C]?|RL:[NOTWU][FD]?|RC:[NUC][DCR]?|CDP:[NLMH][MHD]?|TD:[NLMH]D?|[CIA]R:[NLMH]D?)$/;

// Associative arrays mapping each metric value to the constant defined in the CVSSv2 scoring formula in the CVSSv2 v2.0
// specification.

CVSSv2.Weight = {
  AV:   { L: 0.395, A: 0.646, N: 1},
  AC:   { H: 0.35,  M: 0.61,  L: 0.71},
  Au:   { M: 0.45,  S: 0.56,  N: 0.704},
  CIA:  { N: 0,     P: 0.275, C: 0.660},                   // C, I and A have the same weights

  E:    { ND: 1,    U: 0.85,  POC: 0.9, F: 0.95, H: 1},
  RL:   { ND: 1,    OF: 0.87, TF: 0.90, W: 0.95, U: 1},
  RC:   { ND: 1,    UC: 0.90, UR: 0.95, C: 1},

  CDP:  { ND: 0,    N: 0,     L: 0.1,   LM: 0.3, MH: 0.4, H: 0.5},
  TD:   { ND: 1,    N: 0,     L: 0.25,  M: 0.75, H: 1},
  CIAR: { ND: 1,    L: 0.5,   M: 1,     H: 1.51}           // CR, IR and AR have the same weights
};


// Severity rating bands, as defined in the CVSSv2 v2.0 specification.

CVSSv2.severityRatings  = [
                          { name: "",             bottom: 0.0, top:  0.0},
                          { name: "1.0-info",     bottom: 0.1, top:  1.9},
                          { name: "2.0-low",      bottom: 2.0, top:  3.9},
                          { name: "3.0-medium",   bottom: 4.0, top:  6.9},
                          { name: "4.0-high",     bottom: 7.0, top: 10.0}];


/* ** CVSSv2.calculateCVSSFromMetrics **
 *
 * Takes Base, Temporal and Environmental metric values as individual parameters. Their values are in the short format
 * defined in the CVSSv2 v2.0 standard definition of the Vector String. For example, the AccessComplexity parameter
 * should be either "H", "M" or "L".
 *
 * Returns Base, Temporal and Environmental scores, severity ratings, and an overall Vector String. All Base metrics
 * are required to generate this output. All Temporal and Environmental metric values are optional. Any that are not
 * passed default to "ND" ("Not Defined").
 *
 * The output is an object which always has a property named "success".
 *
 * If no errors are encountered, success is Boolean "true", and the following other properties are defined containing
 * scores, severities and a vector string:
 *   baseMetricScore, baseSeverity,
 *   temporalMetricScore, temporalSeverity,
 *   environmentalMetricScore, environmentalSeverity,
 *   vectorString
 *
 * If errors are encountered, success is Boolean "false", and the following other properties are defined:
 *   errorType - a string indicating the error. Either:
 *                 "MissingBaseMetric", if at least one Base metric has not been defined; or
 *                 "UnknownMetricValue", if at least one metric value is invalid.
 *   errorMetrics - an array of strings representing the metrics at fault. The strings are abbreviated versions of the
 *                  metrics, as defined in the CVSSv2 v2.0 standard definition of the Vector String.
 */
CVSSv2.calculateCVSSFromMetrics = function (
  AttackVector, AccessComplexity, Authentication, Confidentiality, Integrity, Availability,
  Exploitability, RemediationLevel, ReportConfidence,
  CollateralDamagePotential, TargetDistribution,
  ConfidentialityRequirement, IntegrityRequirement, AvailabilityRequirement) {

  // If input validation fails, this array is populated with strings indicating which metrics failed validation.
  var badMetrics = [];

  // ENSURE ALL BASE METRICS ARE DEFINED
  //
  // We need values for all Base Score metrics to calculate scores.
  // If any Base Score parameters are undefined, create an array of missing metrics and return it with an error.

  if (typeof AttackVector       === "undefined" || AttackVector       === "") { badMetrics.push("AV"); }
  if (typeof AccessComplexity   === "undefined" || AccessComplexity   === "") { badMetrics.push("AC"); }
  if (typeof Authentication     === "undefined" || Authentication     === "") { badMetrics.push("Au"); }
  if (typeof Confidentiality    === "undefined" || Confidentiality    === "") { badMetrics.push("C");  }
  if (typeof Integrity          === "undefined" || Integrity          === "") { badMetrics.push("I");  }
  if (typeof Availability       === "undefined" || Availability       === "") { badMetrics.push("A");  }

  if (badMetrics.length > 0) {
    return { success: false, errorType: "MissingBaseMetric", errorMetrics: badMetrics };
  }


  // STORE THE METRIC VALUES THAT WERE PASSED AS PARAMETERS
  //
  // Temporal and Environmental metrics are optional, so set them to "ND" ("Not Defined") if no value was passed.

  var AV = AttackVector;
  var AC = AccessComplexity;
  var Au = Authentication;
  var C  = Confidentiality;
  var I  = Integrity;
  var A  = Availability;

  var E =   Exploitability      || "ND";
  var RL =  RemediationLevel    || "ND";
  var RC =  ReportConfidence    || "ND";

  var CDP = CollateralDamagePotential  || "ND";
  var TD  = TargetDistribution         || "ND";

  var CR =  ConfidentialityRequirement || "ND";
  var IR =  IntegrityRequirement       || "ND";
  var AR =  AvailabilityRequirement    || "ND";


  // CHECK VALIDITY OF METRIC VALUES
  //
  // Use the Weight object to ensure that, for every metric, the metric value passed is valid.
  // If any invalid values are found, create an array of their metrics and return it with an error.
  //
  // We therefore need to explicitly allow a value of "ND" for Environmental metrics.

  if (!CVSSv2.Weight.AV.hasOwnProperty(AV))   { badMetrics.push("AV"); }
  if (!CVSSv2.Weight.AC.hasOwnProperty(AC))   { badMetrics.push("AC"); }
  if (!CVSSv2.Weight.Au.hasOwnProperty(Au))   { badMetrics.push("Au"); }
  if (!CVSSv2.Weight.CIA.hasOwnProperty(C))   { badMetrics.push("C"); }
  if (!CVSSv2.Weight.CIA.hasOwnProperty(I))   { badMetrics.push("I"); }
  if (!CVSSv2.Weight.CIA.hasOwnProperty(A))   { badMetrics.push("A"); }

  if (!CVSSv2.Weight.E.hasOwnProperty(E))     { badMetrics.push("E"); }
  if (!CVSSv2.Weight.RL.hasOwnProperty(RL))   { badMetrics.push("RL"); }
  if (!CVSSv2.Weight.RC.hasOwnProperty(RC))   { badMetrics.push("RC"); }

  if (!CVSSv2.Weight.CDP.hasOwnProperty(CDP)) { badMetrics.push("CDP"); }
  if (!CVSSv2.Weight.TD.hasOwnProperty(TD))   { badMetrics.push("TD"); }

  if (!CVSSv2.Weight.CIAR.hasOwnProperty(CR)) { badMetrics.push("CR"); }
  if (!CVSSv2.Weight.CIAR.hasOwnProperty(IR)) { badMetrics.push("IR"); }
  if (!CVSSv2.Weight.CIAR.hasOwnProperty(AR)) { badMetrics.push("AR"); }

  if (badMetrics.length > 0) {
    return { success: false, errorType: "UnknownMetricValue", errorMetrics: badMetrics };
  }



  // GATHER WEIGHTS FOR ALL METRICS

  var metricWeightAV  = CVSSv2.Weight.AV    [AV];
  var metricWeightAC  = CVSSv2.Weight.AC    [AC];
  var metricWeightAu  = CVSSv2.Weight.Au    [Au];

  var metricWeightC   = CVSSv2.Weight.CIA   [C];
  var metricWeightI   = CVSSv2.Weight.CIA   [I];
  var metricWeightA   = CVSSv2.Weight.CIA   [A];

  var metricWeightE   = CVSSv2.Weight.E     [E];
  var metricWeightRL  = CVSSv2.Weight.RL    [RL];
  var metricWeightRC  = CVSSv2.Weight.RC    [RC];

  var metricWeightCDP = CVSSv2.Weight.CDP   [CDP];
  var metricWeightTD  = CVSSv2.Weight.TD    [TD];

  var metricWeightCR  = CVSSv2.Weight.CIAR  [CR];
  var metricWeightIR  = CVSSv2.Weight.CIAR  [IR];
  var metricWeightAR  = CVSSv2.Weight.CIAR  [AR];


  // CALCULATE THE CVSSv2 BASE SCORE

  var baseScore;
  var exploitabalitySubScore = CVSSv2.exploitabilityCoefficient * metricWeightAV * metricWeightAC * metricWeightAu;
  var impactSubScore = CVSSv2.impactCoefficient * (1 - ((1 - metricWeightC) * (1 - metricWeightI) * (1 - metricWeightA)));

  if (impactSubScore === 0) {
    baseScore = 0;
  } else {
    baseScore = CVSSv2.round1(((0.6 * impactSubScore) + (0.4 * exploitabalitySubScore) - 1.5) * 1.176);
  }

  // console.log('ExpSubScore:' + exploitabalitySubScore);
  // console.log('ImpSubScore:' + impactSubScore);
  // console.log('Base:' + baseScore);

  // CALCULATE THE CVSSv2 TEMPORAL SCORE

  var temporalScore = CVSSv2.round1(baseScore * metricWeightE * metricWeightRL * metricWeightRC);


  // CALCULATE THE CVSSv2 ENVIRONMENTAL SCORE
  //
  // - envExploitabalitySubScore recalculates the Base Score Exploitability sub-score using any modified values from the
  //   Environmental metrics group in place of the values specified in the Base Score, if any have been defined.
  // - envAdjustedImpactSubScore recalculates the Base Score Impact sub-score using any modified values from the
  //   Environmental metrics group in place of the values specified in the Base Score, and any additional weightings
  //   given in the Environmental metrics group.

  var envbaseScore;
  var envModifiedImpactSubScore = Math.min ( 10.41 * (1 - (
                                               (1 - metricWeightC * metricWeightCR) *
                                               (1 - metricWeightI * metricWeightIR) *
                                               (1 - metricWeightA * metricWeightAR))), 10);

  if ( envModifiedImpactSubScore === 0) {
    envbaseScore = ((0.6 * envModifiedImpactSubScore) + (0.4 * exploitabalitySubScore) - 1.5) * 0;
  } else {
    envbaseScore = ((0.6 * envModifiedImpactSubScore) + (0.4 * exploitabalitySubScore) - 1.5) * 1.176;
  }
  var envtemporalScore = envbaseScore * metricWeightE * metricWeightRL * metricWeightRC;
  var envScore = CVSSv2.round1((envtemporalScore + (10 - envtemporalScore) * metricWeightCDP) * metricWeightTD);

  // console.log('ModImpsubScore:' + envModifiedImpactSubScore);
  // console.log('envBaseScore:' + envbaseScore);
  // console.log('envTemporalScore:' + envtemporalScore);

  // CONSTRUCT THE VECTOR STRING

  var vectorString =
    CVSSv2.CVSSVersionIdentifier +
    "AV:" + AV +
    "/AC:" + AC +
    "/Au:" + Au +
    "/C:"  + C +
    "/I:"  + I +
    "/A:"  + A;

  if (E  !== "ND")  {vectorString = vectorString + "/E:"  + E;}
  if (RL !== "ND")  {vectorString = vectorString + "/RL:" + RL;}
  if (RC !== "ND")  {vectorString = vectorString + "/RC:" + RC;}

  if (CDP !== "ND") {vectorString = vectorString + "/CDP:" + CDP;}
  if (TD  !== "ND") {vectorString = vectorString + "/TD:"  + TD;}

  if (CR  !== "ND") {vectorString = vectorString + "/CR:"  + CR;}
  if (IR  !== "ND") {vectorString = vectorString + "/IR:"  + IR;}
  if (AR  !== "ND") {vectorString = vectorString + "/AR:"  + AR;}


  // Return an object containing the scores for all three metric groups, and an overall vector string.

  return {
    success: true,
    baseMetricScore: baseScore.toFixed(1),
    baseSeverity: CVSSv2.severityRating( baseScore.toFixed(1) ),

    temporalMetricScore: temporalScore.toFixed(1),
    temporalSeverity: CVSSv2.severityRating( temporalScore.toFixed(1) ),

    environmentalMetricScore: envScore.toFixed(1),
    environmentalSeverity: CVSSv2.severityRating( envScore.toFixed(1) ),

    vectorString: vectorString
  };
};




/* ** CVSSv2.calculateCVSSFromVector **
 *
 * Takes Base, Temporal and Environmental metric values as a single string in the Vector String format defined
 * in the CVSSv2 2.0 standard definition of the Vector String.
 *
 * Returns Base, Temporal and Environmental scores, severity ratings, and an overall Vector String. All Base metrics
 * are required to generate this output. All Temporal and Environmental metric values are optional. Any that are not
 * passed default to "ND" ("Not Defined").
 *
 * See the comment for the CVSSv2.calculateCVSSFromMetrics function for details on the function output. In addition to
 * the error conditions listed for that function, this function can also return:
 *   "MalformedVectorString", if the Vector String passed is does not conform to the format in the standard; or
 *   "MultipleDefinitionsOfMetric", if the Vector String is well formed but defines the same metric (or metrics),
 *                                  more than once.
 */
CVSSv2.calculateCVSSFromVector = function ( vectorString ) {

  var metricValues = {
    AV:  undefined, AC:  undefined, Au:  undefined,
    C:   undefined, I:   undefined, A:   undefined,
    E:   undefined, RL:  undefined, RC:  undefined,
    CDP: undefined, TD:  undefined,
    CR:  undefined, IR:  undefined, AR:  undefined
  };

  // If input validation fails, this array is populated with strings indicating which metrics failed validation.
  var badMetrics = [];

  if (!CVSSv2.vectorStringRegex_20.test(vectorString)) {
    return { success: false, errorType: "MalformedVectorString" };
  }

  var metricNameValue = vectorString.substring(CVSSv2.CVSSVersionIdentifier.length).split("/");

  for (var i in metricNameValue) {
    if (metricNameValue.hasOwnProperty(i)) {

      var singleMetric = metricNameValue[i].split(":");

      if (typeof metricValues[singleMetric[0]] === "undefined") {
        metricValues[singleMetric[0]] = singleMetric[1];
      } else {
        badMetrics.push(singleMetric[0]);
      }
    }
  }

  if (badMetrics.length > 0) {
    return { success: false, errorType: "MultipleDefinitionsOfMetric", errorMetrics: badMetrics };
  }
  var score = CVSSv2.calculateCVSSFromMetrics (
    metricValues.AV,  metricValues.AC,  metricValues.Au,
    metricValues.C,   metricValues.I,   metricValues.A,
    metricValues.E,   metricValues.RL,  metricValues.RC,
    metricValues.CDP, metricValues.TD,
    metricValues.CR,  metricValues.IR,  metricValues.AR);
  return {
    success: score?.success,
    AV: metricValues.AV,  AC: metricValues.AC,  Au: metricValues.Au,
    C: metricValues.C,   I: metricValues.I,   A: metricValues.A,
    E: metricValues.E,   RL: metricValues.RL,  RC: metricValues.RC,
    CDP: metricValues.CDP, TD: metricValues.TD,
    CR: metricValues.CR,  IR: metricValues.IR,  AR: metricValues.AR,
    ...score,
  };
};




/* ** CVSSv2.round1 **
 *
 * Rounds up the number passed as a parameter to 1 decimal place and returns the result.
 *
 * Standard JavaScript errors thrown when arithmetic operations are performed on non-numbers will be returned if the
 * given input is not a number.
 */
CVSSv2.round1 = function (d) {
  return Math.round (d * 10) / 10;
};




/* ** CVSSv2.severityRating **
 *
 * Given a CVSSv2 score, returns the name of the severity rating as defined in the CVSSv2 standard.
 * The input needs to be a number between 0.0 to 10.0, to one decimal place of precision.
 *
 * The following error values may be returned instead of a severity rating name:
 *   NaN (JavaScript "Not a Number") - if the input is not a number.
 *   undefined - if the input is a number that is not within the range of any defined severity rating.
 */
CVSSv2.severityRating = function (score) {
  var severityRatingLength = CVSSv2.severityRatings.length;

  var validatedScore = Number(score);

  if (isNaN(validatedScore)) {
    return validatedScore;
  }

  for (var i = 0; i < severityRatingLength; i++) {
    if (score >= CVSSv2.severityRatings[i].bottom && score <= CVSSv2.severityRatings[i].top) {
      return CVSSv2.severityRatings[i].name;
    }
  }

  return undefined;
};

module.exports = {
  CVSSv2
}
